import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { colors, Icons } from '../../../../packages';

import { mockType, profileSectionIds } from '../../../provider/profile/Profile.constants';
import ProfileCard from '../../../../pages/provider/profile/components/profile-card';
import EditCredentials from '../../../../pages/provider/profile/components/credentials';
import EditGeneralInfo from '../../../../pages/provider/profile/components/general-info';
import EditApproach from '../../../../pages/provider/profile/components/approach';
import EditClinicAffiliation from '../../../../pages/provider/profile/components/clinic-affiliation';
import EditEducation from '../../../../pages/provider/profile/components/education';
import EditAvailableStates from '../../../../pages/provider/profile/components/available-states';
import { getProfile } from '../../../../redux/modules/profile/selectors';
import { selectProviderServicesState } from '../../../../redux/modules/appointment/selectors';
import { profileActionCreators } from '../../../../redux/modules/profile';
import { selectProviderSchedule } from '../../../../redux/modules/schedule/selectors';
import { scheduleActionCreators } from '../../../../redux/modules/schedule';
import { profileSections } from './GeneralInfo.mock';
import { stateActionCreators } from '../../../../redux/modules/state';
import { ResetPassword } from '../reset-password/ResetPassword';
import { getAuth } from '../../../../redux/modules/auth/selectors';
import ScheduleLink from '../../../provider/schedule/components/schedule-link/ScheduleLink';
import Header from '../../../../components/v2/Header';
import { showSnackbar } from '../../../../redux/modules/snackbar';
import {
  getAllModalities,
  getPatientModalities,
  updatePatientModalities,
} from '../../../../services/profile/profile.service';
import { ModalitiesDrawer } from '../../../provider/sessions/sign-off-v4/sections/ModalitiesDrawer';

const GeneralInfo = () => {
  const dispatch = useDispatch();
  const { isAdmin } = useSelector(getAuth);
  const { services } = useSelector(selectProviderServicesState);
  const { providerDetails, profile } = useSelector(getProfile);
  const { providerId } = useParams();
  const [editSection, setEditSection] = useState(null);
  const [sections, setSections] = useState([]);
  const [errorMsg, setErrorMsg] = useState(null);
  const [, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const providerSchedule = useSelector(selectProviderSchedule);
  const [allModalities, setAllModalities] = useState([]);
  const [modalities, setModalities] = useState([]);
  const [isModalitiesDrawerOpen, setIsModalitiesDrawerOpen] = useState(false);
  const onEdit = sectionId => () => {
    setErrorMsg(null);
    if (sectionId === profileSectionIds.modalities) {
      setIsModalitiesDrawerOpen(true);
    } else {
      setEditSection(sectionId);
    }
  };
  const onCloseEdit = () => {
    setEditSection(null);
  };

  useEffect(() => {
    dispatch(profileActionCreators.fetchProviderDetails(providerId || profile.providerId));
    dispatch(profileActionCreators.fetchProviderProfile());
    dispatch(stateActionCreators.fetchPayerGroups());
    void fetchAllModalities();
    void fetchProviderModalities();
  }, []);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setIsLoading(false);
    }, 1000);
    return () => clearTimeout(timeout);
  }, []);

  const fetchAllModalities = async () => {
    try {
      const response = await getAllModalities();
      if (response?.data) {
        setAllModalities(response.data.modalities || []);
      }
    } catch (error) {
      console.error('Error fetching modalities:', error);
    }
  };

  const fetchProviderModalities = async () => {
    try {
      const currentProviderId = providerId || profile.providerId;
      const response = await getPatientModalities(currentProviderId);
      if (response?.data) {
        setModalities(response.data.modalities || []);
      }
    } catch (error) {
      console.error('Error fetching provider modalities:', error);
    }
  };

  const handleModalitiesSubmit = async updatedModalities => {
    try {
      const currentProviderId = providerId || profile.providerId;
      setModalities(updatedModalities);
      await updatePatientModalities(currentProviderId, {
        modalities: updatedModalities,
      });
      dispatch(
        showSnackbar({
          snackType: 'success',
          snackMessage: 'Modalities updated successfully.',
        }),
      );
      setIsModalitiesDrawerOpen(false);
    } catch (error) {
      console.error('Error updating modalities:', error);
      dispatch(
        showSnackbar({
          snackType: 'error',
          snackMessage: 'Failed to update modalities. Please try again.',
        }),
      );
    }
  };

  const getContent = id => {
    switch (id) {
      case profileSectionIds.general: {
        return {
          description: providerDetails?.bio || '',
          profile: {
            avatar: providerDetails?.profileImage || '',
            name: providerDetails?.fullName || '',
            firstName: providerDetails?.firstName || '',
            lastName: providerDetails?.lastName || '',
            role: providerDetails?.providerProfile?.providerRole || '',
            activeInApp: providerDetails?.active,
            acceptingNewPatients: providerDetails?.acceptingNewPatients,
            specialities: providerDetails?.speciality,
            emailAddress: providerDetails?.emailAddress,
            phoneNumber: providerDetails?.phoneNumber,
            twoFAEnabled: providerDetails?.twoFAEnabled,
            designation: providerDetails?.designation,
            signOffRole: providerDetails?.signOffRole || '',
            npi: providerDetails?.npi || '',
            acceptingAdolescents: providerDetails?.acceptingAdolescents || false,
            prescribesControlled: providerDetails?.providerProfile?.prescribesControlled || false,
            prescribesMAT: providerDetails?.providerProfile?.prescribesMAT || false,
            prescribesPsych: providerDetails?.providerProfile?.prescribesPsych || false,
            taxId: providerDetails?.taxId || '',
            billingProvider: providerDetails?.billingProvider,
            providerAvailabilityCalendar: providerDetails?.providerAvailabilityCalendar,
            practitionerAddress: {
              state: providerDetails?.practitionerAddress?.state || '',
              city: providerDetails?.practitionerAddress?.city || '',
              zipCode: providerDetails?.practitionerAddress?.zipCode || '',
              zipPlusFourCode: providerDetails?.practitionerAddress?.zipPlusFourCode || '',
              address1: providerDetails?.practitionerAddress?.address1 || '',
              address2: providerDetails?.practitionerAddress?.address2 || '',
            },
            enabled: providerDetails?.providerProfile?.enabled,
            cashNoShowRate: providerDetails?.cashNoShowRate,
            commercialNoShowRate: providerDetails?.commercialNoShowRate,
            medicaidNoShowRate: providerDetails?.medicaidNoShowRate,
          },
        };
      }
      case profileSectionIds.approach: {
        return {
          description: providerDetails?.approach || 'N/A',
        };
      }
      case profileSectionIds.availableStates: {
        return {
          insurances: providerDetails?.insurances?.sort((a, b) => (a > b ? 1 : -1)) || [],
        };
      }
      case profileSectionIds.speciality: {
        return {
          badges: providerDetails?.speciality?.sort((a, b) => (a > b ? 1 : -1)) || [],
        };
      }
      case profileSectionIds.certifications: {
        return {
          badges: providerDetails?.providerProfile?.certifications?.sort((a, b) => (a > b ? 1 : -1)) || [],
        };
      }
      case profileSectionIds?.credentials: {
        return {
          badges: providerDetails?.providerProfile?.credentials?.sort((a, b) => (a > b ? 1 : -1)) || [],
        };
      }
      case profileSectionIds?.clinicAffiliation: {
        return {
          affiliation: {
            avatar: providerDetails?.providerProfile?.affiliationImage,
            title: providerDetails?.providerProfile?.affiliationName?.[0],
            description: providerDetails?.providerProfile?.affiliationPlace?.[0],
            startDate: providerDetails?.providerProfile?.affiliationStartDate,
            endDate: providerDetails?.providerProfile?.affiliationEndDate,
          },
        };
      }
      case profileSectionIds.pastEmployment: {
        return {
          affiliation: {
            avatar: providerDetails?.providerProfile?.employmentImage,
            title: providerDetails?.providerProfile?.employmentName?.[0],
            description: providerDetails?.providerProfile?.employmentPlace?.[0],
            startDate: providerDetails?.providerProfile?.employmentStartDate,
            endDate: providerDetails?.providerProfile?.employmentEndDate,
          },
        };
      }
      case profileSectionIds?.education: {
        return {
          affiliation: {
            avatar: providerDetails?.providerProfile?.educationImage,
            title: providerDetails?.providerProfile?.educationName?.[0],
            description: providerDetails?.providerProfile?.educationDescription,
            startDate: providerDetails?.providerProfile?.educationStartDate,
            endDate: providerDetails?.providerProfile?.educationEndDate,
          },
        };
      }
      case profileSectionIds.generalAvailability: {
        return {
          workingDays: {
            sunday: null,
            monday: null,
            tuesday: null,
            wednesday: null,
            thursday: null,
            friday: null,
            saturday: null,
          },
        };
      }
      case profileSectionIds.resetPassword: {
        return {
          emailAddress: providerDetails?.emailAddress,
        };
      }
      case profileSectionIds.modalities: {
        return {
          badges: modalities || [],
        };
      }
      default:
        return { services };
    }
  };

  const onUpdateSuccess = () => {
    dispatch(showSnackbar({ snackType: 'success', snackMessage: 'Information updated successfully!' }));
    setIsSaving(false);
    onCloseEdit();
  };
  const onSubmit = payload => {
    try {
      setErrorMsg(null);
      const isActivePayload = payload?.active
        ? JSON.parse(payload?.active)
        : providerDetails?.active || false;
      const acceptingNewPatientsPayload = payload?.acceptingNewPatients
        ? JSON.parse(payload?.acceptingNewPatients)
        : providerDetails?.acceptingNewPatients || false;
      const data = {
        affiliationImage: payload?.affiliationImage || providerDetails?.providerProfile?.affiliationImage,
        affiliationName: payload?.affiliationName || providerDetails?.providerProfile?.affiliationName,
        affiliationPlace: payload?.affiliationPlace || providerDetails?.providerProfile?.affiliationPlace,
        approach: payload?.approach || providerDetails?.approach,
        certifications: payload?.certifications || providerDetails?.providerProfile?.certifications,
        credentials: payload?.credentials || providerDetails?.providerProfile?.credentials,
        educationDescription:
          payload?.educationDescription || providerDetails?.providerProfile?.educationDescription,
        educationEndDate: payload?.educationEndDate || providerDetails?.providerProfile?.educationEndDate,
        profilePicture: payload.profilePhoto || providerDetails?.profileImage,
        educationImage: payload?.educationImage || providerDetails?.providerProfile?.educationImage,
        educationName: payload?.educationName || providerDetails?.providerProfile?.educationName,
        educationPlace: payload?.educationPlace || providerDetails?.providerProfile?.educationPlace,
        educationStartDate:
          payload?.educationStartDate || providerDetails?.providerProfile?.educationStartDate,
        employmentEndDate: payload?.employmentEndDate || providerDetails?.providerProfile?.employmentEndDate,
        employmentImage: payload?.employmentImage || providerDetails?.providerProfile?.employmentImage,
        employmentName: payload?.employmentName || providerDetails?.providerProfile?.employmentName,
        employmentPlace: payload?.employmentPlace || providerDetails?.providerProfile?.employmentPlace,
        employmentStartDate:
          payload?.employmentStartDate || providerDetails?.providerProfile?.employmentStartDate,
        firstName: payload?.firstName || providerDetails?.firstName,
        lastName: payload?.lastName || providerDetails?.lastName,
        fullName: payload?.fullName || providerDetails?.fullName,
        active: payload?.active !== null ? isActivePayload : providerDetails?.active,
        acceptingNewPatients:
          payload?.acceptingNewPatients !== null
            ? acceptingNewPatientsPayload
            : providerDetails?.acceptingNewPatients,
        bio: payload?.bio || providerDetails?.bio,
        providerRole: payload?.providerRole || providerDetails?.providerProfile?.providerRole,
        providerAvailabilityCalendar: payload?.providerAvailabilityCalendar || '',
        twoFAEnabled: payload?.twoFAEnabled !== null ? payload?.twoFAEnabled : providerDetails?.twoFAEnabled,
        enabled: payload?.enabled !== null ? payload?.enabled : providerDetails?.providerProfile?.enabled,
        speciality: payload?.speciality?.length > 0 ? payload?.speciality : providerDetails?.speciality,
        phoneNumber: payload?.phoneNumber !== null ? payload?.phoneNumber : providerDetails?.phoneNumber,
        signOffRole: payload?.signOffRole || providerDetails?.signOffRole,
        designation: payload?.designation !== null ? payload?.designation : providerDetails?.designation,
        npi: payload?.npi !== null ? payload?.npi : providerDetails?.npi,
        acceptingAdolescents:
          payload?.acceptingAdolescents !== null
            ? payload.acceptingAdolescents
            : providerDetails?.acceptingAdolescents,
        prescribesControlled:
          payload?.prescribesControlled !== null
            ? payload.prescribesControlled
            : providerDetails?.providerProfile?.prescribesControlled,
        prescribesMAT:
          payload?.prescribesMAT !== null
            ? payload.prescribesMAT
            : providerDetails?.providerProfile?.prescribesMAT,
        prescribesPsych:
          payload?.prescribesPsych !== null
            ? payload.prescribesPsych
            : providerDetails?.providerProfile?.prescribesPsych,
        taxId: payload?.taxId ?? providerDetails.taxId,
        billingProvider:
          payload?.billingProvider !== null ? payload.billingProvider : providerDetails?.billingProvider,
        practitionerAddress: {
          state: payload?.state ?? providerDetails?.practitionerAddress?.state,
          city: payload?.city ?? providerDetails?.practitionerAddress?.city,
          zipCode: payload?.zipCode ?? providerDetails?.practitionerAddress?.zipCode,
          zipPlusFourCode: payload?.zipPlusFourCode ?? providerDetails?.practitionerAddress?.zipPlusFourCode,
          address1: payload?.address1 ?? providerDetails?.practitionerAddress?.address1,
          address2: payload?.address2 ?? providerDetails?.practitionerAddress?.address2,
        },
        cashNoShowRate: payload?.cashNoShowRate ?? providerDetails?.cashNoShowRate,
        commercialNoShowRate: payload?.commercialNoShowRate ?? providerDetails?.commercialNoShowRate,
        medicaidNoShowRate: payload?.medicaidNoShowRate ?? providerDetails?.medicaidNoShowRate,
      };
      if (payload?.timezone && payload?.timezone !== providerSchedule?.timezone) {
        dispatch(
          scheduleActionCreators.updateProviderSchedule({
            providerId: providerDetails?.providerId,
            data: {
              timezone: payload?.timezone || providerSchedule?.timezone,
            },
          }),
        );
      }
      dispatch(
        profileActionCreators.updateProviderGeneralInfo({
          id: providerDetails?.providerId,
          data,
          onSuccess: onUpdateSuccess,
        }),
      );
    } catch (err) {
      setErrorMsg(err.data?.errors?.[0]?.endUserMessage || 'Unable to update data');
      setIsSaving(false);
    }
  };

  const editContent = getContent(editSection);

  useEffect(() => {
    let newSections;
    if (isAdmin) {
      newSections = profileSections?.map(item => ({
        ...item,
        content: getContent(item.id),
      }));
    } else {
      newSections = profileSections
        ?.filter(
          section =>
            section.id === profileSectionIds.general ||
            section.id === profileSectionIds.approach ||
            section.id === profileSectionIds.speciality ||
            section.id === profileSectionIds.certifications ||
            section.id === profileSectionIds.credentials ||
            section.id === profileSectionIds.modalities,
        )
        ?.map(item => ({
          ...item,
          content: getContent(item.id),
        }));
    }
    setSections(newSections);
  }, [providerDetails, services, modalities]);
  return (
    <>
      {isLoading ? (
        <Box display="flex" justifyContent="center" paddingY="10px">
          <Icons className="rotate linear infinite" glyph="in-progress" color={colors.primary} />
        </Box>
      ) : (
        <>
          {!isAdmin && <Header label="General information" />}
          <Box display="flex" flexDirection="column" gap="16px">
            {!isAdmin && <ScheduleLink />}
            {sections?.map((section, idx) => (
              <ProfileCard {...section} key={idx} onEdit={onEdit(section.id)} />
            ))}
          </Box>
        </>
      )}
      <EditGeneralInfo
        isOpen={editSection === profileSectionIds.general}
        onClose={onCloseEdit}
        error={errorMsg}
        profile={{
          description: editContent?.description,
          ...editContent?.profile,
          timezone: (providerSchedule && providerSchedule.timezone) || '',
        }}
        onSubmit={onSubmit}
      />
      <EditApproach
        isOpen={editSection === profileSectionIds.approach}
        onClose={onCloseEdit}
        error={errorMsg}
        onSubmit={onSubmit}
        description={editContent.description}
      />
      <EditAvailableStates
        isOpen={editSection === profileSectionIds.availableStates}
        onClose={onCloseEdit}
        providerId={providerDetails?.providerId}
        states={editContent.badges}
      />
      <EditCredentials
        type={editSection}
        title={profileSections.find(value => value?.id === editSection)?.title}
        isOpen={[
          profileSectionIds.credentials,
          profileSectionIds.certifications,
          profileSectionIds.speciality,
        ].includes(editSection)}
        onClose={onCloseEdit}
        error={errorMsg}
        selectedItems={editContent.badges}
        items={mockType[editSection]}
        onUpdate={onSubmit}
      />
      <EditEducation
        isOpen={editSection === profileSectionIds.pastEmployment}
        onClose={onCloseEdit}
        error={errorMsg}
        onUpdate={onSubmit}
        data={editContent.affiliation}
      />
      <EditEducation
        isOpen={editSection === profileSectionIds.education}
        onClose={onCloseEdit}
        isEducation
        onUpdate={onSubmit}
        error={errorMsg}
        data={editContent.affiliation}
      />
      <EditClinicAffiliation
        isOpen={editSection === profileSectionIds.clinicAffiliation}
        onClose={onCloseEdit}
        error={errorMsg}
        onUpdate={onSubmit}
        clinicAffiliation={editContent.affiliation}
      />

      <ResetPassword
        isOpen={editSection === profileSectionIds.resetPassword}
        onClose={onCloseEdit}
        email={editContent.emailAddress}
      />

      {isModalitiesDrawerOpen && (
        <ModalitiesDrawer
          isOpen={isModalitiesDrawerOpen}
          onClose={() => setIsModalitiesDrawerOpen(false)}
          allModalities={allModalities}
          modalities={modalities}
          onSubmit={handleModalitiesSubmit}
        />
      )}
    </>
  );
};

export { GeneralInfo };
